// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelAnalysisPage
 * @package @geenius/analytics/react
 * @description Page-level React composition for configuring and reviewing
 * analytics funnel analysis.
 */

import {
  type AnalyticsEvent,
  analyzeFunnel,
  buildAnalyticsSeoMeta,
  type FunnelResult,
  type FunnelStep,
} from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/react";
import type React from "react";
import { useMemo, useState } from "react";
import { FunnelChart } from "../components/FunnelChart";
import { StatsCard } from "../components/StatsCard";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import {
  ANALYTICS_MOTION_DELAYS,
  FadeIn,
  getFadeInTransition,
} from "../lib/motion";
import type { AnalyticsSeoMeta } from "./AnalyticsDashboardPage";

/**
 * Props for rendering the React funnel analysis page.
 *
 * @property name Human-readable funnel name used when analyzing raw events.
 * @property steps Ordered funnel step configuration shown in the builder.
 * @property events Optional raw events used by the built-in analyze action.
 * @property result Optional precomputed funnel result to visualize.
 * @property onAnalyze Called after the built-in analyze action computes a result.
 * @property isLoading Whether the analysis surface should show loading state.
 * @property error Optional page-level error message or error object.
 * @property onRetry Optional retry handler rendered with the error state.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property className Optional class name merged into the page wrapper.
 */
export interface FunnelAnalysisPageProps {
  name?: string;
  steps?: FunnelStep[];
  events?: AnalyticsEvent[];
  result?: FunnelResult | null;
  onAnalyze?: (result: FunnelResult) => void;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => React.ReactNode;
  className?: string;
}

function formatPercent(value: number | undefined): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

function FunnelPageError({
  error,
  onRetry,
}: {
  error: Error | string;
  onRetry?: () => void;
}): React.ReactElement {
  return (
    <div role="alert" data-analytics-funnel-error>
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="text-lg">
            Unable to load funnel analysis
          </CardTitle>
          <CardDescription>{getErrorMessage(error)}</CardDescription>
        </CardHeader>
        {onRetry && (
          <CardContent className="pt-4">
            <Button type="button" variant="secondary" onClick={onRetry}>
              Retry
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

/**
 * Renders a page-level funnel builder and conversion analysis surface.
 *
 * @param props Funnel configuration, raw events, and optional precomputed result.
 * @returns A React page composition for funnel analysis workflows.
 */
export function FunnelAnalysisPage({
  name = "Conversion funnel",
  steps = [],
  events = [],
  result,
  onAnalyze,
  isLoading,
  error,
  onRetry,
  renderSeoMeta,
  className,
}: FunnelAnalysisPageProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const [computedResult, setComputedResult] = useState<FunnelResult | null>(
    null,
  );
  const activeResult = result ?? computedResult;
  const canAnalyze = events.length > 0 && steps.length > 0;
  const seoMeta = buildAnalyticsSeoMeta({
    title: "Funnel Analysis",
    description:
      "Configure conversion steps and inspect funnel completion across analytics events.",
    path: "/analytics/funnels",
    keywords: ["analytics", "funnels", "conversion", "events"],
  });
  const stepRows = useMemo(
    () =>
      steps.map((step, index) => ({
        ...step,
        order: index + 1,
        propertyCount: Object.keys(step.properties ?? {}).length,
      })),
    [steps],
  );

  const runAnalysis = (): void => {
    const nextResult = analyzeFunnel(events, steps, name);
    setComputedResult(nextResult);
    onAnalyze?.(nextResult);
  };

  return (
    <>
      {renderSeoMeta ? renderSeoMeta(seoMeta) : null}
      <div
        className={cn(
          "flex min-h-screen flex-col gap-6 bg-background p-6 text-foreground",
          className,
        )}
        data-analytics-page="funnel-analysis"
      >
        <FadeIn transition={getFadeInTransition()}>
          <header className="flex flex-col gap-1" data-analytics-page-header>
            <h1 className="text-3xl font-bold tracking-tight">
              Funnel Analysis
            </h1>
            <p className="text-sm text-muted-foreground">
              Build ordered conversion steps and review drop-off across the
              journey.
            </p>
          </header>
        </FadeIn>

        {error ? (
          <FunnelPageError error={error} onRetry={onRetry} />
        ) : (
          <>
            <FadeIn
              transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.stats)}
              className="grid gap-4 sm:grid-cols-3"
              data-analytics-funnel-kpis
            >
              {isLoading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <Card key={index} data-analytics-funnel-kpi-loading>
                    <CardContent className="grid gap-3 pt-6">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-8 w-32" />
                    </CardContent>
                  </Card>
                ))
              ) : (
                <>
                  <StatsCard
                    label="Entries"
                    value={activeResult?.totalEntries.toLocaleString() ?? "0"}
                    icon="IN"
                  />
                  <StatsCard
                    label={copy.t("analytics.dashboard.conversions")}
                    value={
                      activeResult?.totalConversions.toLocaleString() ?? "0"
                    }
                    icon="OK"
                  />
                  <StatsCard
                    label={copy.t("analytics.dashboard.conversionRate")}
                    value={formatPercent(activeResult?.overallConversionRate)}
                    icon="%"
                  />
                </>
              )}
            </FadeIn>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
              <FadeIn
                transition={getFadeInTransition(
                  ANALYTICS_MOTION_DELAYS.overview,
                )}
              >
                <Card data-analytics-funnel-builder>
                  <CardHeader className="pb-0">
                    <CardTitle data-analytics-section-title>{name}</CardTitle>
                    <CardDescription>
                      {stepRows.length.toLocaleString()} configured steps
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 pt-4">
                    {stepRows.length === 0 ? (
                      <EmptyState
                        bordered={false}
                        compact
                        title="No funnel steps configured."
                      />
                    ) : (
                      <div className="grid gap-3">
                        {stepRows.map((step) => (
                          <article
                            key={`${step.order}-${step.event}`}
                            className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3"
                            data-analytics-funnel-step
                          >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                              {step.order}
                            </span>
                            <div className="min-w-0">
                              <strong className="text-sm text-foreground">
                                {step.name}
                              </strong>
                              <p className="text-xs text-muted-foreground">
                                {step.event} - {step.propertyCount} property
                                filters
                              </p>
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                    <div>
                      <Button
                        type="button"
                        variant="primary"
                        disabled={!canAnalyze || isLoading}
                        onClick={runAnalysis}
                      >
                        Analyze funnel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>

              <FadeIn
                transition={getFadeInTransition(ANALYTICS_MOTION_DELAYS.events)}
              >
                {isLoading ? (
                  <div data-analytics-funnel-loading>
                    <Card>
                      <CardContent className="grid gap-4 pt-6">
                        <Skeleton className="h-5 w-36" />
                        <Skeleton className="h-48 w-full" />
                      </CardContent>
                    </Card>
                  </div>
                ) : activeResult ? (
                  <FunnelChart result={activeResult} />
                ) : (
                  <Card data-analytics-funnel-empty>
                    <CardContent className="pt-6">
                      <EmptyState
                        bordered={false}
                        compact
                        title={copy.t("analytics.funnel.empty")}
                      />
                    </CardContent>
                  </Card>
                )}
              </FadeIn>
            </div>
          </>
        )}
      </div>
    </>
  );
}
